import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';

// Component imports  
import { SuccessStoryDialogComponent } from '../success-story-dialog/success-story-dialog.component';
import { SuccessStoryItemComponent, SuccessStory } from '../../components/success-story-item/success-story-item.component';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner.component';

@Component({
  selector: 'app-success-stories',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    MessageModule,
    SkeletonModule,
    ToastModule,
    SuccessStoryItemComponent,
    LoadingSpinnerComponent
  ],
  providers: [DialogService, MessageService],
  templateUrl: './success-stories.component.html',
  styleUrls: ['./success-stories.component.css']
})
export class SuccessStoriesComponent implements OnInit, OnDestroy {
  // Loading state
  loading = signal(true);
  
  successStories: SuccessStory[] = [];
  private ref: DynamicDialogRef | undefined;
  private timeout?: number;

  constructor(
    private dialogService: DialogService,
    private translateService: TranslateService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadSampleData();
  }

  ngOnDestroy(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  private loadSampleData(): void {
    if (this.timeout) clearTimeout(this.timeout);
    
    this.loading.set(true);
    this.successStories = [];
    
    this.timeout = window.setTimeout(() => {
      // Sample data for demonstration
      this.successStories = [
        {
          id: '1',
          coverImage: 'https://via.placeholder.com/400x250/3B82F6/FFFFFF?text=Success+Story+1',
          title: 'Revolutionary DeFi Platform',
          description: 'A groundbreaking decentralized finance platform that transformed how users interact with cryptocurrencies, achieving over $100M in total value locked within the first year.',
          linkUrl: 'https://example.com/defi-platform'
        },
        {
          id: '2',
          coverImage: 'https://via.placeholder.com/400x250/10B981/FFFFFF?text=Success+Story+2',
          title: 'AI-Powered Trading Bot',
          description: 'An innovative AI trading solution that delivers consistent returns for investors through advanced machine learning algorithms and real-time market analysis.',
          linkUrl: 'https://example.com/ai-trading'
        },
        {
          id: '3',
          coverImage: 'https://via.placeholder.com/400x250/8B5CF6/FFFFFF?text=Success+Story+3',
          title: 'NFT Marketplace Revolution',
          description: 'A cutting-edge NFT marketplace that connects artists and collectors worldwide, featuring unique curation tools and sustainable blockchain technology.',
          linkUrl: 'https://example.com/nft-marketplace'
        }
      ];
      
      this.loading.set(false);
    }, 2000);
  }

  openAddSuccessStoryDialog(): void {
    this.ref = this.dialogService.open(SuccessStoryDialogComponent, {
      header: this.translateService.instant('successStories.addTitle'),
      width: '700px',
      modal: true,
      closable: true
    });

    this.ref.onClose.subscribe((result: any) => {
      if (result && result.success) {
        this.addSuccessStory(result.data);
        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('successStories.success.addTitle'),
          detail: this.translateService.instant('successStories.success.addMessage'),
          life: 4000
        });
      }
    });
  }

  openEditSuccessStoryDialog(story: SuccessStory, index: number): void {
    this.ref = this.dialogService.open(SuccessStoryDialogComponent, {
      header: this.translateService.instant('successStories.editTitle'),
      width: '700px',
      modal: true,
      closable: true,
      data: { successStory: story }
    });

    this.ref.onClose.subscribe((result: any) => {
      if (result && result.success) {
        this.updateSuccessStory(result.data, index);
        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('successStories.success.editTitle'),
          detail: this.translateService.instant('successStories.success.editMessage'),
          life: 4000
        });
      }
    });
  }

  addSuccessStory(story: SuccessStory): void {
    // Generate ID for new story
    story.id = Date.now().toString();
    
    // Add to local array
    this.successStories.push(story);

    // TODO: Here you would typically save to backend
    this.saveSuccessStory(story);
  }

  updateSuccessStory(updatedStory: SuccessStory, index: number): void {
    // Update local array
    this.successStories[index] = updatedStory;

    // TODO: Here you would typically save to backend
    this.saveSuccessStory(updatedStory);
  }

  removeSuccessStory(index: number): void {
    // Remove from local array
    this.successStories.splice(index, 1);

    // TODO: Here you would typically delete from backend
  }

  onEditStory(event: { story: SuccessStory; index: number }): void {
    this.openEditSuccessStoryDialog(event.story, event.index);
  }

  onRemoveStory(event: { story: SuccessStory; index: number }): void {
    this.removeSuccessStory(event.index);
  }

  private saveSuccessStory(story: SuccessStory): void {
    // TODO: Implement actual save logic here
    console.log('Saving success story:', story);
  }
}